import { validURLV2 } from "utils/validations";
import { ESIGN_PLACEMENT } from "@my-scoot/exly-react-component-lib";
import { KRA_DOCUMENTS_REQUIRED_COUNT } from "features/ManageOfferings/modules/OfferingPageSections/modules/KRASubsection/modules/KRAContractUrlFooter/constants/KRAContractFooter.constants";
import {
  PER_TIER_KYC_CONTEXT,
  PER_TIER_KYC_COPY,
  PER_TIER_KYC_ERRORS,
  PER_TIER_KYC_FIELD_KEYS,
  PER_TIER_KYC_METHOD,
  PER_TIER_KYC_MODE,
} from "./PerTierKyc.constants";
import { getUploadedFiles } from "features/ManageOfferings/modules/ManageContractInputSubSection/utils/contractUpload.utils";

export const isCustomKycMode = (mode) => mode === PER_TIER_KYC_MODE.CUSTOM;

export const getRecordKycOverride = (record) =>
  record?.metadata?.[PER_TIER_KYC_FIELD_KEYS.KYC_OVERRIDE] ??
  record?.[PER_TIER_KYC_FIELD_KEYS.KYC_OVERRIDE] ??
  null;

export const getKycStatusForRecord = (record) => {
  if (record?.[PER_TIER_KYC_FIELD_KEYS.KYC_STATUS]) {
    return record[PER_TIER_KYC_FIELD_KEYS.KYC_STATUS];
  }
  const override = getRecordKycOverride(record);
  return isCustomKycMode(override?.[PER_TIER_KYC_FIELD_KEYS.MODE])
    ? PER_TIER_KYC_MODE.CUSTOM
    : PER_TIER_KYC_MODE.INHERIT;
};

export const isCustomKycRecord = (record) =>
  getKycStatusForRecord(record) === PER_TIER_KYC_MODE.CUSTOM;

export const getInitialDraftForRecord = (record) => {
  const override = getRecordKycOverride(record);
  if (isCustomKycMode(override?.[PER_TIER_KYC_FIELD_KEYS.MODE])) {
    return {
      [PER_TIER_KYC_FIELD_KEYS.MODE]: PER_TIER_KYC_MODE.CUSTOM,
      [PER_TIER_KYC_FIELD_KEYS.KYC_TYPE]:
        override?.[PER_TIER_KYC_FIELD_KEYS.KYC_TYPE] ?? null,
      [PER_TIER_KYC_FIELD_KEYS.CONTRACT_PDF_URL]:
        override?.[PER_TIER_KYC_FIELD_KEYS.CONTRACT_PDF_URL] ?? "",
      [PER_TIER_KYC_FIELD_KEYS.ESIGN_PLACEMENT]:
        override?.[PER_TIER_KYC_FIELD_KEYS.ESIGN_PLACEMENT] ??
        ESIGN_PLACEMENT.BEFORE_BOOKING,
      [PER_TIER_KYC_FIELD_KEYS.KRA_CONTRACT_URL]: getUploadedFiles(
        override?.[PER_TIER_KYC_FIELD_KEYS.KRA_CONTRACT_URL]
      ),
      [PER_TIER_KYC_FIELD_KEYS.KYC_WORKFLOW]:
        override?.[PER_TIER_KYC_FIELD_KEYS.KYC_WORKFLOW] ?? "",
      [PER_TIER_KYC_FIELD_KEYS.ENABLED]: Boolean(
        override?.[PER_TIER_KYC_FIELD_KEYS.IS_ESIGN_ENABLED] ||
          override?.[PER_TIER_KYC_FIELD_KEYS.IS_DIGIO_KYC_ENABLED]
      ),
    };
  }
  return {
    [PER_TIER_KYC_FIELD_KEYS.MODE]: PER_TIER_KYC_MODE.INHERIT,
    [PER_TIER_KYC_FIELD_KEYS.KYC_TYPE]: PER_TIER_KYC_METHOD.ESIGN,
    [PER_TIER_KYC_FIELD_KEYS.CONTRACT_PDF_URL]: "",
    [PER_TIER_KYC_FIELD_KEYS.KRA_CONTRACT_URL]: [],
    [PER_TIER_KYC_FIELD_KEYS.KYC_WORKFLOW]: "",
    [PER_TIER_KYC_FIELD_KEYS.ENABLED]: true,
  };
};

export const getModalCopyForContext = (context) => ({
  subtitle:
    PER_TIER_KYC_COPY.SUBTITLE[context] ??
    PER_TIER_KYC_COPY.SUBTITLE[PER_TIER_KYC_CONTEXT.MEMBERSHIP],
  customOption:
    PER_TIER_KYC_COPY.MODE_CUSTOM[context] ??
    PER_TIER_KYC_COPY.MODE_CUSTOM[PER_TIER_KYC_CONTEXT.MEMBERSHIP],
});

export const validateKycDraft = (draft) => {
  const errors = {};
  if (!isCustomKycMode(draft?.[PER_TIER_KYC_FIELD_KEYS.MODE])) {
    return errors;
  }
  const method = draft?.[PER_TIER_KYC_FIELD_KEYS.KYC_TYPE];
  if (
    method !== PER_TIER_KYC_METHOD.ESIGN &&
    method !== PER_TIER_KYC_METHOD.KRA
  ) {
    errors[PER_TIER_KYC_FIELD_KEYS.KYC_TYPE] =
      PER_TIER_KYC_ERRORS.METHOD_REQUIRED;
    return errors;
  }
  if (draft?.[PER_TIER_KYC_FIELD_KEYS.ENABLED] === false) {
    return errors;
  }
  if (method === PER_TIER_KYC_METHOD.ESIGN) {
    const contractUrl = draft?.[PER_TIER_KYC_FIELD_KEYS.CONTRACT_PDF_URL] ?? "";
    if (!contractUrl.trim()) {
      errors[PER_TIER_KYC_FIELD_KEYS.CONTRACT_PDF_URL] =
        PER_TIER_KYC_ERRORS.CONTRACT_REQUIRED;
    } else if (!validURLV2(contractUrl)) {
      errors[PER_TIER_KYC_FIELD_KEYS.CONTRACT_PDF_URL] =
        PER_TIER_KYC_ERRORS.CONTRACT_URL_INVALID;
    }
  } else {
    const docs = getUploadedFiles(
      draft?.[PER_TIER_KYC_FIELD_KEYS.KRA_CONTRACT_URL]
    );
    if (docs.length < KRA_DOCUMENTS_REQUIRED_COUNT) {
      errors[PER_TIER_KYC_FIELD_KEYS.KRA_CONTRACT_URL] =
        PER_TIER_KYC_ERRORS.KRA_DOCS_REQUIRED;
    }
  }
  return errors;
};

export const isSameKycOverride = (current, next) => {
  const currentCustom = isCustomKycMode(
    current?.[PER_TIER_KYC_FIELD_KEYS.MODE]
  );
  const nextCustom = isCustomKycMode(next?.[PER_TIER_KYC_FIELD_KEYS.MODE]);
  if (currentCustom !== nextCustom) return false;
  if (!nextCustom) return true;
  const sameList = (left, right) => {
    const a = left ?? [];
    const b = right ?? [];
    return (
      a.length === b.length && a.every((value, index) => value === b[index])
    );
  };
  const currentEsignEnabled =
    current?.[PER_TIER_KYC_FIELD_KEYS.KYC_TYPE] ===
      PER_TIER_KYC_METHOD.ESIGN &&
    Boolean(current?.[PER_TIER_KYC_FIELD_KEYS.IS_ESIGN_ENABLED]);
  const nextEsignEnabled =
    next?.[PER_TIER_KYC_FIELD_KEYS.KYC_TYPE] === PER_TIER_KYC_METHOD.ESIGN &&
    Boolean(next?.[PER_TIER_KYC_FIELD_KEYS.IS_ESIGN_ENABLED]);
  // Placement only matters when both sides run enabled e-signing; missing
  // values mean BEFORE_BOOKING (same default the backend applies).
  const placementMatters = currentEsignEnabled && nextEsignEnabled;
  const currentPlacement =
    current?.[PER_TIER_KYC_FIELD_KEYS.ESIGN_PLACEMENT] ??
    ESIGN_PLACEMENT.BEFORE_BOOKING;
  const nextPlacement =
    next?.[PER_TIER_KYC_FIELD_KEYS.ESIGN_PLACEMENT] ??
    ESIGN_PLACEMENT.BEFORE_BOOKING;
  return (
    current?.[PER_TIER_KYC_FIELD_KEYS.KYC_TYPE] ===
      next?.[PER_TIER_KYC_FIELD_KEYS.KYC_TYPE] &&
    Boolean(current?.[PER_TIER_KYC_FIELD_KEYS.IS_ESIGN_ENABLED]) ===
      Boolean(next?.[PER_TIER_KYC_FIELD_KEYS.IS_ESIGN_ENABLED]) &&
    Boolean(current?.[PER_TIER_KYC_FIELD_KEYS.IS_DIGIO_KYC_ENABLED]) ===
      Boolean(next?.[PER_TIER_KYC_FIELD_KEYS.IS_DIGIO_KYC_ENABLED]) &&
    (current?.[PER_TIER_KYC_FIELD_KEYS.CONTRACT_PDF_URL] ?? "") ===
      (next?.[PER_TIER_KYC_FIELD_KEYS.CONTRACT_PDF_URL] ?? "") &&
    (current?.[PER_TIER_KYC_FIELD_KEYS.KYC_WORKFLOW] ?? "") ===
      (next?.[PER_TIER_KYC_FIELD_KEYS.KYC_WORKFLOW] ?? "") &&
    (!placementMatters || currentPlacement === nextPlacement) &&
    sameList(
      getUploadedFiles(current?.[PER_TIER_KYC_FIELD_KEYS.KRA_CONTRACT_URL]),
      getUploadedFiles(next?.[PER_TIER_KYC_FIELD_KEYS.KRA_CONTRACT_URL])
    )
  );
};

// Inherit intentionally persists only `{ mode: "inherit" }` — the backend
// treats it as "clear override / use offering default", and checkout ignores
// non-custom modes, so orphaned kyc_type / contract URLs can never leak
// through even if a prior custom config existed.
export const buildKycOverrideFromDraft = (draft) => {
  if (!isCustomKycMode(draft?.[PER_TIER_KYC_FIELD_KEYS.MODE])) {
    return { [PER_TIER_KYC_FIELD_KEYS.MODE]: PER_TIER_KYC_MODE.INHERIT };
  }
  const method = draft?.[PER_TIER_KYC_FIELD_KEYS.KYC_TYPE];
  const isEnabled = draft?.[PER_TIER_KYC_FIELD_KEYS.ENABLED] !== false;
  const isEsign = isEnabled && method === PER_TIER_KYC_METHOD.ESIGN;
  const isKra = isEnabled && method === PER_TIER_KYC_METHOD.KRA;
  const builtOverride = {
    [PER_TIER_KYC_FIELD_KEYS.MODE]: PER_TIER_KYC_MODE.CUSTOM,
    [PER_TIER_KYC_FIELD_KEYS.KYC_TYPE]: method,
    [PER_TIER_KYC_FIELD_KEYS.IS_ESIGN_ENABLED]: isEsign,
    [PER_TIER_KYC_FIELD_KEYS.IS_DIGIO_KYC_ENABLED]: isKra,
    [PER_TIER_KYC_FIELD_KEYS.CONTRACT_PDF_URL]: isEsign
      ? draft?.[PER_TIER_KYC_FIELD_KEYS.CONTRACT_PDF_URL] ?? ""
      : "",
    [PER_TIER_KYC_FIELD_KEYS.KRA_CONTRACT_URL]: isEsign
      ? []
      : getUploadedFiles(draft?.[PER_TIER_KYC_FIELD_KEYS.KRA_CONTRACT_URL]),
    // Workflow applies to KRA only; blank means "use the offering template".
    // Clearing it on method switch keeps stale values out of esign tiers.
    [PER_TIER_KYC_FIELD_KEYS.KYC_WORKFLOW]: isKra
      ? draft?.[PER_TIER_KYC_FIELD_KEYS.KYC_WORKFLOW] ?? ""
      : "",
  };
  // Placement applies to enabled e-signing only; omitting it otherwise keeps
  // stored overrides free of stale values after a method switch.
  if (isEsign) {
    builtOverride[PER_TIER_KYC_FIELD_KEYS.ESIGN_PLACEMENT] =
      draft?.[PER_TIER_KYC_FIELD_KEYS.ESIGN_PLACEMENT] ??
      ESIGN_PLACEMENT.BEFORE_BOOKING;
  }
  return builtOverride;
};
